class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.cartFooterAnimation();
    this.rechargeChangeOption();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        
        // Only trap focus if we have a valid container
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Wait for the next frame to ensure DOM is updated
    requestAnimationFrame(() => {
      // Wait another frame to ensure the new DOM is fully rendered
      requestAnimationFrame(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
        
        // Wait for the drawer to be fully opened before reinitializing
        this.addEventListener('transitionend', () => {
          this.cartFooterAnimation();
          this.rechargeChangeOption();
        }, { once: true });
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  cartFooterAnimation() {
      const footer = this.querySelector('.drawer__footer');
     
      const scrollArea = document.querySelector('.drawer__inner');

      if (!scrollArea || !footer) return;

      function updateShadow() {
        const atTop = scrollArea.scrollTop === 0;
        const atBottom = scrollArea.scrollTop + scrollArea.clientHeight >= scrollArea.scrollHeight;

        if (atTop || atBottom) {
          footer.classList.remove('has-shadow');
        } else {
          footer.classList.add('has-shadow');
        }
      }

      scrollArea.addEventListener('scroll', updateShadow);
      updateShadow(); // Run on load in case it's already scrolled

  }

  rechargeChangeOption() {
    const modal = document.getElementById('selling-plan-modal');
    const modalPlans = document.getElementById('modal-selling-plans');

    // Money formatter helper function
    const formatMoney = (cents) => {
      if (typeof cents === 'string') cents = cents.replace('.', '')
        const symbol = window.mitoq.currency.symbol ?? '$'
        return `${symbol}${(cents / 100).toFixed(2)}`
    };

    // Function to show loading state for specific item
    const showLoading = (cartItemKey) => {
        const itemLoading = document.querySelector(`[data-cart-item-key="${cartItemKey}"] .cart-item-loading-container`);
        if (itemLoading) {
            itemLoading.style.display = 'flex';
        }
    };

    // Function to hide loading state for specific item
    const hideLoading = (cartItemKey) => {
        const itemLoading = document.querySelector(`[data-cart-item-key="${cartItemKey}"] .cart-item-loading-container`);
        if (itemLoading) {
            itemLoading.style.display = 'none';
        }
    };

    // Function to update cart
    const updateCart = async (cartItemKey, variantId, sellingPlanId = null) => {
        try {
            // Close modal immediately
            modal.style.display = 'none';
            
            // Show loading state for specific item
            showLoading(cartItemKey);

            // If we have a selling plan ID, we need to verify if the variant supports it
            if (sellingPlanId) {
                // Get the current cart item to find the product handle
                const cartResponse = await fetch('/cart.js');
                const cartData = await cartResponse.json();
                const currentItem = cartData.items.find(item => item.key === cartItemKey);
                
                if (currentItem) {
                    // Get product data to check variant selling plans
                    const productResponse = await fetch(`/products/${currentItem.handle}.js`);
                    const productData = await productResponse.json();
                    
                    // Find a variant that supports the selected selling plan
                    const compatibleVariant = productData.variants.find(variant => {
                        return variant.selling_plan_allocations.some(
                            allocation => allocation.selling_plan_id === parseInt(sellingPlanId)
                        );
                    });

                    // If we found a compatible variant, use it instead
                    if (compatibleVariant) {
                        variantId = compatibleVariant.id;
                    } else {
                        // If no compatible variant found, fall back to one-time purchase
                        sellingPlanId = null;
                    }
                }
            }

            // First remove the current item
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: cartItemKey,
                    quantity: 0
                })
            });

            // Then add the new item
            const addData = {
                items: [{
                    id: variantId,
                    quantity: 1
                }]
            };

            // If there's a selling plan ID, add it to the item
            if (sellingPlanId) {
                addData.items[0].selling_plan = sellingPlanId;
            }

            const addResponse = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(addData)
            });

            if (!addResponse.ok) {
                throw new Error('Failed to add item to cart');
            }

            // Get fresh cart data
            const cartResponse = await fetch('?section_id=cart-drawer');
            if (!cartResponse.ok) {
                throw new Error('Failed to get updated cart data');
            }
            
            const cartHTML = await cartResponse.text();
            const parsedHTML = new DOMParser().parseFromString(cartHTML, 'text/html');
            const newDrawerInner = parsedHTML.querySelector('.drawer__inner');
            
            if (!newDrawerInner) {
                throw new Error('Could not find drawer inner in response');
            }

            // Update the cart drawer content
            const drawerInnerElement = this.querySelector('.drawer__inner');
            if (!drawerInnerElement) {
                throw new Error('Could not find drawer inner element');
            }

            drawerInnerElement.innerHTML = newDrawerInner.innerHTML;

            // Re-attach event listeners
            this.cartFooterAnimation();
            this.rechargeChangeOption();

        } catch (error) {
            console.error('Error updating cart:', error);
            hideLoading(cartItemKey);
            throw error; // Re-throw to handle in the calling code
        } finally {
            hideLoading(cartItemKey);
        }
    };

    this.querySelectorAll('.option-change').forEach(button => {
        button.addEventListener('click', function() {
            const plansJSON = this.dataset.sellingPlans;
            const productTitle = this.dataset.title;
            const variantsJSON = this.dataset.variants;
            const currentVariantId = this.dataset.currentVariantId;
            const currentSellingPlanId = this.dataset.sellingPlanId || '';
            const cartItemKey = this.dataset.key;

            let groups;
            let variants;
            try {
                groups = JSON.parse(unescape(plansJSON));
                variants = JSON.parse(unescape(variantsJSON));
            } catch (e) {
                groups = [];
                variants = [];
            }

            modalPlans.innerHTML = '';

            // Create subscription section first
            if (groups.length > 0) {
                const subscriptionSection = document.createElement('div');
                subscriptionSection.className = 'purchase-option-section subscribe-method';
                subscriptionSection.innerHTML = '<h3>Subscribe & Save</h3>';
                const plansList = document.createElement('ul');
                plansList.className = 'plans-list';

                groups.forEach(group => {
                    group.selling_plans.forEach(plan => {
                        const li = document.createElement('li');
                        
                        // Find the variant that supports this selling plan
                        const compatibleVariant = variants.find(variant => {
                            return variant.selling_plan_allocations.some(
                                allocation => allocation.selling_plan_id === plan.id
                            );
                        });

                        if (!compatibleVariant) return; // Skip if no compatible variant found

                        // Get the selling plan allocation for this specific plan
                        const sellingPlanAllocation = compatibleVariant.selling_plan_allocations.find(
                            allocation => allocation.selling_plan_id === plan.id
                        );

                        // Get the correct prices
                        const originalPrice = compatibleVariant.price;
                        const finalPrice = sellingPlanAllocation ? sellingPlanAllocation.price : originalPrice;
                        const hasDiscount = finalPrice < originalPrice;

                        // Check if this is the current selling plan AND variant
                        const isCurrentPlan = currentSellingPlanId && 
                                             parseInt(currentSellingPlanId) === plan.id;
                        
                        // Check if the variant is sold out
                        const isSoldOut = !compatibleVariant.available;

                        li.innerHTML = `
                            <label class="${isSoldOut ? 'sold-out' : ''}">
                                <input type="radio" name="purchase_option" value="sub_${plan.id}"
                                    data-variant-id="${compatibleVariant.id}"
                                    data-selling-plan-id="${plan.id}"
                                    ${isCurrentPlan ? 'checked' : ''}
                                    ${isSoldOut ? 'disabled' : ''}>
                                <div class="option-details">
                                    <span class="option-name">${plan.name}</span>
                                    <div class="price-details">
                                        ${hasDiscount ? `<s class="original-price">${formatMoney(originalPrice)}</s>` : ''}
                                        <span class="final-price">${formatMoney(finalPrice)} <span class="price--symbol">${Shopify.currency.active}</span></span>
                                        ${isSoldOut ? '<span class="sold-out-text">Sold out</span>' : ''}
                                    </div>
                                </div>
                            </label>
                        `;
                        plansList.appendChild(li);
                    });
                });

                subscriptionSection.appendChild(plansList);
                modalPlans.appendChild(subscriptionSection);
            }

            // Create one-time purchase section
            const oneTimeSection = document.createElement('div');
            oneTimeSection.className = 'purchase-option-section';
            oneTimeSection.innerHTML = '<h3>One-time purchase</h3>';
            const variantsList = document.createElement('ul');
            variantsList.className = 'variants-list';

            // Filter variants that don't have selling plans
            const oneTimeVariants = variants.filter(variant => {
                // Check if the variant has any selling plan allocations
                return !variant.selling_plan_allocations || variant.selling_plan_allocations.length === 0;
            });

            oneTimeVariants.forEach(variant => {
                const li = document.createElement('li');
                const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
                
                // Get display name from the hidden variant data element
                let formattedTitle = variant.title;
                const variantDataElement = document.querySelector(`.variant-data[data-id="${variant.id}"]`);
                if (variantDataElement && variantDataElement.dataset.displayName) {
                    formattedTitle = variantDataElement.dataset.displayName;
                } else {
                    // If no display name, use the month supply format
                    if (variant.title.includes('1M')) {
                        formattedTitle = '1-month supply';
                    } else if (variant.title.includes('3M')) {
                        formattedTitle = '3-month supply';
                    } else if (variant.title.includes('6M')) {
                        formattedTitle = '6-month supply';
                    }
                }
                
                // Only check one-time purchase if there's no selling plan AND it's the current variant
                const isCurrentVariant = !currentSellingPlanId && 
                                       parseInt(currentVariantId) === variant.id;

                // Check if the variant is sold out
                const isSoldOut = !variant.available;

                li.innerHTML = `
                    <label class="${isSoldOut ? 'sold-out' : ''}">
                        <input type="radio" name="purchase_option" value="one_time_${variant.id}" 
                            data-variant-id="${variant.id}"
                            ${isCurrentVariant ? 'checked' : ''}
                            ${isSoldOut ? 'disabled' : ''}>
                        <div class="option-details">
                            <span class="option-name">${formattedTitle}</span>
                            <div class="price-details">
                                ${hasDiscount ? `<s class="original-price">${formatMoney(variant.compare_at_price)}</s>` : ''}
                                <span class="final-price">${formatMoney(variant.price)}</span>
                            </div>
                        </div>
                    </label>
                `;
                variantsList.appendChild(li);
            });

            oneTimeSection.appendChild(variantsList);
            modalPlans.appendChild(oneTimeSection);

            // Add confirm button
            const confirmButton = document.createElement('button');
            confirmButton.className = 'confirm-change-button button';
            confirmButton.innerHTML = '<span>Confirm change</span>';
            confirmButton.addEventListener('click', () => {
                const selectedOption = modalPlans.querySelector('input[name="purchase_option"]:checked');
                if (selectedOption) {
                    // Add loading state to button
                    confirmButton.classList.add('is-loading');
                    confirmButton.disabled = true;
                    
                    const variantId = selectedOption.dataset.variantId;
                    const sellingPlanId = selectedOption.dataset.sellingPlanId || null;
                    updateCart(cartItemKey, variantId, sellingPlanId);
                }
            });
            modalPlans.appendChild(confirmButton);

            modal.style.display = 'flex';
        });
    });

    document.querySelector('.selling-close-modal').addEventListener('click', () => {
        modal.style.display = 'none';
    });

    document.addEventListener('click', function(e) {
        if (e.target.id === 'selling-plan-modal') {
            modal.style.display = 'none';
        }
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
